import React, { useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import history from '../../services/history'

import { signOut } from '../../store/modules/auth/actions'
import { RootState } from '../../store/modules/rootReducer'

import { learningApi } from '../../services/api'

import { BsBell, BsBoxArrowLeft, BsPerson } from 'react-icons/bs'
import { TbMoonFilled, TbSunHigh, TbSunMoon } from 'react-icons/tb'
import { BiHelpCircle, BiShoppingBag } from 'react-icons/bi'
import { ReactComponent as MdOutlineWorkspacePremium } from '../../assets/icons/premium.svg'

import {
  HeaderButton,
  UserDropdown,
  QuestionDropdown,
  Badge,
  Container,
  SwitchDetail,
} from './styles'
import { switchColorMode } from '../../store/modules/theme/actions'
import { Link } from 'react-router-dom'
import useSWR from 'swr'
import { useClickOutside } from '../../hooks/use-click-outside'
import {
  TLabeledMessage,
  TLabeledNotification,
  TMergedNotifications,
  TNotificationResponse,
} from './types'
import { TMessageOrigin } from '../../tracking/types'

interface HeaderOptionsProps {
  className?: string
}

const fetcher = (token: string) => (url: string) =>
  learningApi
    .get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res.data)

const HeaderOptions: React.FC<HeaderOptionsProps> = ({ className }) => {
  const dispatch = useDispatch()

  const [openUserMenu, setOpenUserMenu] = useState<boolean>(false)
  const [openNotification, setOpenNotification] = useState<boolean>(false)

  const { profile } = useSelector((state: RootState) => state.student)
  const token = localStorage.getItem('token')
  const { isDarkMode } = useSelector((state: RootState) => state.theme)
  const { subscriptions, status } = useSelector((state: RootState) => state.subscription)

  const userMenuRef = useClickOutside(() => setOpenUserMenu(false))
  const notificationRef = useClickOutside(() => setOpenNotification(false))

  const key = token ? '/notification' : null

  const { data } = useSWR<TNotificationResponse>(key, fetcher(token), {
    fallbackData: { notifications: [], messages: [] },
  })

  const mergedNotifications: TMergedNotifications = useMemo(() => {
    const labeledNotifications = data.notifications.map((item) => ({
      ...item,
      type: 'notification' as const,
    }))
    const labeledMessages = data.messages.map((item) => ({ ...item, type: 'message' as const }))

    const merged = [...labeledNotifications, ...labeledMessages]

    merged.sort((a, b) => Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)))

    return merged
  }, [data.messages, data.notifications])

  const total = mergedNotifications.length
  const isEmpty = total === 0

  const isUserLifetimeSubscriber = useMemo(() => {
    if (status === 'success') {
      return subscriptions.some(
        (subscription) =>
          subscription.name === 'Assinatura Vitalícia' ||
          subscription.name === 'Assinatura Vitalícia Premium'
      )
    } else {
      return false
    }
  }, [status, subscriptions])

  const renderNotification = (notification: TLabeledNotification) => (
    <Link
      key={notification._id}
      to={`/duvida-respondida/${notification.studentQuestion._id._id}/${notification.studentQuestion._id.code}`}
      className="link"
    >
      <p>
        <b className="date">{new Date(notification.createdAt).toLocaleDateString()}</b> - A sua
        dúvida <b>#{notification.studentQuestion._id.code}</b> foi respondida pelo professor{' '}
        <b>{notification.professor._id.name}</b>.
      </p>
      <span></span>
    </Link>
  )

  const renderMessage = (message: TLabeledMessage) => (
    <Link
      key={message._id}
      to={{
        pathname: `/recado/${message._id}`,
        state: { origin: 'Aba de Notificacao' as TMessageOrigin },
      }}
      className="link"
    >
      <p>
        <b className="date">{new Date(message.createdAt).toLocaleDateString()}</b> - Você tem um
        novo recado do professor <b>{message.professor.name}</b>. Assunto: <b>{message.subject}</b>
      </p>
    </Link>
  )

  const renderList = () =>
    mergedNotifications.map((item) => {
      if (item.type === 'message') return renderMessage(item)
      if (item.type === 'notification') return renderNotification(item)

      return null
    })

  return (
    <Container className={className}>
      <div ref={notificationRef} style={{ position: 'relative' }}>
        <HeaderButton onClick={() => setOpenNotification((state) => !state)}>
          {total > 0 && (
            <Badge>
              <span>{total}</span>
            </Badge>
          )}
          <BsBell />
        </HeaderButton>
        {openNotification && (
          <QuestionDropdown>
            {renderList()}
            {isEmpty && (
              <ul>
                <p>Nenhuma nova mensagem</p>
              </ul>
            )}
          </QuestionDropdown>
        )}
      </div>
      <div ref={userMenuRef} style={{ position: 'relative' }}>
        <HeaderButton id="userMenu" isAvatar onClick={() => setOpenUserMenu((state) => !state)}>
          <img
            src={
              profile?.avatar ||
              `https://ui-avatars.com/api/?name=${profile?.name}&=rounded=true&background=ff6600&color=FFF`
            }
            alt="Avatar"
            id="userMenu"
          />
          {isUserLifetimeSubscriber && (
            <div className="lifetimeBadge">
              <MdOutlineWorkspacePremium />
            </div>
          )}
        </HeaderButton>
        {openUserMenu && (
          <UserDropdown id="userMenu">
            <ul id="userMenu">
              <li
                id="userMenu"
                className={window.location.pathname === '/config/geral' ? 'selected' : ''}
                onClick={() => history.push('/config/geral')}
              >
                <BsPerson size={20} /> Meus dados
              </li>
              <li
                id="userMenu"
                className={window.location.pathname === '/config/payment' ? 'selected' : ''}
                onClick={() => history.push('/config/payment')}
              >
                <BiShoppingBag size={20} /> Meus pedidos
              </li>
              <li
                id="userMenu"
                className={window.location.pathname === '/config/help' ? 'selected' : ''}
                onClick={() => history.push('/config/help')}
              >
                <BiHelpCircle size={20} /> Ajuda
              </li>
              <li
                id="userMenu"
                onClick={() => dispatch(switchColorMode({ isDarkMode: !isDarkMode }))}
              >
                <TbSunMoon size={20} /> Modo
                <SwitchDetail isDarkMode={isDarkMode}>
                  <div className="slider">
                    <TbSunHigh size={16} />
                    <TbMoonFilled size={16} />
                  </div>
                </SwitchDetail>
              </li>
              <li id="userMenu" onClick={() => dispatch(signOut())}>
                <BsBoxArrowLeft size={20} /> Sair
              </li>
            </ul>
          </UserDropdown>
        )}
      </div>
    </Container>
  )
}

export default HeaderOptions
